/**
 * Contenedor del Creador de reportes: alterna entre el inicio (biblioteca de reportes del motor)
 * y el editor. Cada reporte abierto monta un editor nuevo (key distinta), así no arrastra estado
 * del anterior. Aquí viven las acciones de la biblioteca: abrir, duplicar, eliminar y compartir.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../AuthContext';
import { useNavigation } from '../../NavigationContext';
import ReportCreatorWorkspace from './ReportCreatorWorkspace';
import CreatorHome from './widget/CreatorHome';
import ShareReportDialog from './widget/ShareReportDialog';
import { confirmLeaveCreator } from './widget/leaveGuard';
import { ink, studio } from './studioUi';

/** Diálogo para el nombre de la copia. */
function DuplicateDialog({ report, onCancel, onConfirm }) {
    const [name, setName] = useState(`Copia de ${report.reporteNombre}`);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const submit = async (event) => {
        event.preventDefault();
        if (!name.trim()) return;
        setSaving(true);
        setError('');
        try {
            await onConfirm(name.trim());
        } catch (err) {
            setError(err.message || 'No se pudo duplicar el reporte.');
            setSaving(false);
        }
    };
    return createPortal(
        <div style={styles.backdrop} onMouseDown={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}>
            <form onSubmit={submit} style={styles.dialog} role="dialog" aria-modal="true" aria-label="Duplicar reporte">
                <p style={studio.kicker}>Duplicar</p>
                <h3 style={styles.title}>{report.reporteNombre}</h3>
                <p style={studio.hint}>La copia queda como borrador en el mismo cliente y proyecto, sin accesos compartidos.</p>
                <label>
                    <span style={studio.fieldLabel}>Nombre de la copia</span>
                    <input
                        autoFocus
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        style={studio.control}
                    />
                </label>
                {error && <div style={styles.error}>{error}</div>}
                <div style={styles.foot}>
                    <button type="button" onClick={onCancel} disabled={saving} style={studio.ghostBtn}>Cancelar</button>
                    <button type="submit" disabled={saving || !name.trim()} style={studio.primaryBtn}>
                        {saving ? 'Duplicando…' : 'Duplicar'}
                    </button>
                </div>
            </form>
        </div>,
        document.body
    );
}

const ReportCreatorShell = ({
    styles: panelStyles,
    fetchWithAuth,
    API_URL,
    clients,
    projects,
    refreshAllData,
    editorBlocked = false,
    editorBlockedNotice = null,
}) => {
    const { user, userPermissions, withBusy } = useAuth();
    const { openReport } = useNavigation();
    const [mode, setMode] = useState('home');
    const [library, setLibrary] = useState({ reports: [], loading: true, error: '' });
    const [session, setSession] = useState(null);
    const [shareTarget, setShareTarget] = useState(null);
    const [duplicateTarget, setDuplicateTarget] = useState(null);

    const loadLibrary = useCallback(async () => {
        setLibrary(prev => ({ ...prev, loading: true, error: '' }));
        try {
            const res = await fetchWithAuth(`${API_URL}/api/creator/library`);
            const data = await res.json();
            setLibrary({ reports: data.reports || [], loading: false, error: '' });
        } catch (err) {
            setLibrary(prev => ({ ...prev, loading: false, error: err.message || 'No se pudo cargar la biblioteca.' }));
        }
    }, [fetchWithAuth, API_URL]);

    useEffect(() => { loadLibrary(); }, [loadLibrary]);

    /** Abre un reporte en el editor con su configuración completa (incluye borradores). */
    const openEditor = useCallback(async (reportId) => {
        if (!reportId) return;
        if (mode === 'editor' && !confirmLeaveCreator()) return;
        await withBusy(async () => {
            try {
                const res = await fetchWithAuth(`${API_URL}/api/creator/reports/${reportId}`);
                const data = await res.json();
                setSession({ key: `${reportId}-${Date.now()}`, report: data.report, capabilities: data.capabilities });
                setMode('editor');
            } catch (err) {
                alert(err.message || 'No se pudo abrir el reporte.');
            }
        }, 'Abriendo reporte…');
    }, [mode, fetchWithAuth, API_URL, withBusy]);

    const newReport = useCallback((preset = {}) => {
        if (mode === 'editor' && !confirmLeaveCreator()) return;
        setSession({ key: `new-${Date.now()}`, report: null, capabilities: null, preset });
        setMode('editor');
    }, [mode]);

    const goHome = useCallback(() => {
        if (!confirmLeaveCreator()) return;
        setMode('home');
        setSession(null);
        loadLibrary();
    }, [loadLibrary]);

    const handleOpen = (report) => {
        if (report.isDraft) {
            openEditor(report.id);
            return;
        }
        openReport(report);
    };

    const handleDuplicate = async (name) => {
        const res = await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${duplicateTarget.id}/copy`, {
            method: 'POST',
            silent: true,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ newReportName: name, copyResponsibles: false }),
        });
        await res.json().catch(() => ({}));
        setDuplicateTarget(null);
        await loadLibrary();
        if (refreshAllData) refreshAllData();
    };

    const handleDelete = async (report) => {
        const ok = window.confirm(
            `¿Eliminar «${report.reporteNombre}»?\n\nSe borran también sus accesos, comentarios, ajustes y datos guardados. No se puede deshacer.`
        );
        if (!ok) return;
        await withBusy(async () => {
            try {
                await fetchWithAuth(`${API_URL}/admin/reportes-maestros/${report.id}`, { method: 'DELETE', silent: true });
                await loadLibrary();
                if (refreshAllData) refreshAllData();
            } catch (err) {
                alert(err.message || 'No se pudo eliminar el reporte.');
            }
        }, 'Eliminando reporte…');
    };

    const canCreate = Boolean(userPermissions?.reports_custom_creator);

    return (
        <>
            {mode === 'home' ? (
                <CreatorHome
                    reports={library.reports}
                    loading={library.loading}
                    error={library.error}
                    onRetry={loadLibrary}
                    clients={clients}
                    canCreate={canCreate}
                    perms={userPermissions}
                    uid={user?.uid}
                    onNew={newReport}
                    onOpen={handleOpen}
                    onEdit={(report) => openEditor(report.id)}
                    onShare={setShareTarget}
                    onDuplicate={setDuplicateTarget}
                    onDelete={handleDelete}
                />
            ) : editorBlocked ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {editorBlockedNotice}
                    <button type="button" onClick={goHome} style={{ ...studio.ghostBtn, alignSelf: 'flex-start' }}>
                        Volver al inicio del Creador
                    </button>
                </div>
            ) : (
                <ReportCreatorWorkspace
                    key={session?.key || 'new'}
                    styles={panelStyles}
                    fetchWithAuth={fetchWithAuth}
                    API_URL={API_URL}
                    clients={clients}
                    projects={projects}
                    refreshAllData={refreshAllData}
                    libraryReports={library.reports}
                    initialReport={session?.report || null}
                    initialCapabilities={session?.capabilities || null}
                    initialPreset={session?.preset || null}
                    onGoHome={goHome}
                    onOpenReport={openEditor}
                    onNewReport={() => newReport({})}
                    onSaved={loadLibrary}
                />
            )}

            <ShareReportDialog
                open={Boolean(shareTarget)}
                report={shareTarget}
                onClose={() => setShareTarget(null)}
                onChanged={() => { loadLibrary(); if (refreshAllData) refreshAllData(); }}
                fetchWithAuth={fetchWithAuth}
                API_URL={API_URL}
            />
            {duplicateTarget && (
                <DuplicateDialog
                    report={duplicateTarget}
                    onCancel={() => setDuplicateTarget(null)}
                    onConfirm={handleDuplicate}
                />
            )}
        </>
    );
};

const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(28, 29, 33, 0.38)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
    },
    dialog: {
        width: 'min(460px, 100%)',
        backgroundColor: ink.surface,
        borderRadius: 16,
        boxShadow: '0 20px 50px rgba(28, 29, 33, 0.25)',
        padding: '22px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        boxSizing: 'border-box',
    },
    title: { margin: 0, fontSize: '1.15rem', fontWeight: 600, color: ink.text },
    error: { padding: '10px 12px', borderRadius: 10, backgroundColor: ink.dangerSoft, color: ink.danger, fontSize: '0.86rem' },
    foot: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 },
};

export default ReportCreatorShell;
